import React, { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import Select from 'react-select';
import Async from 'react-select/async';
import { ExcludeSwitch } from '../../pages/SearchPage/Filter/ExcludeSwitch';
import { GeoGroupFilter } from '../../pages/SearchPage/Filter/GeoGroupFilter';
import { TagsHierarchyChooser } from '../TagsHierarchyChooser/TagsHierarchyChooser';
import { ALL_STATUSES_QUERY, IoCTypeOptionsList, affiliationTypesOptions } from '../../utils/actors';
import { platformIconsMapping } from '../../utils/platforms';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import { Tooltip } from 'react-tooltip';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';

interface ActorsBehaviorGroupProps {
  form: any;
  actorGroupRef?: React.RefObject<any>;
  actorRef?: React.RefObject<any>;
  isDisabled?: boolean;
}

export const ActorsBehaviorGroup: React.FC<ActorsBehaviorGroupProps> = ({
  form,
  actorGroupRef,
  actorRef,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [countriesTop, setCountriesTop] = useState([]);
  const [defaultGroupOptions, setDefaultGroupOptions] = useState([]);

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showCountryGroupsFilter = featureFlags?.includes(
    'country_groups_filter',
  );
  const showFeedsFilter = featureFlags?.includes('show_feeds_filter');
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');
  const showTagsFilter = featureFlags?.includes('show_tags_filter');
  
  const getAllCountries = useCallback(() => {
    API.fetch('GET', `/API/v1/countries?page=1&size=100`).then((data) => {
      setCountriesTop(
        data.objects.slice(0, 10).map((country: any) => {
          return { value: country.id, label: country.name };
        }),
      );
    }).catch((error) => showRequestError(error, t('Countries')));
  }, [t]);

  useEffect(getAllCountries, [getAllCountries]);

   const fetchGroupsOptions = useCallback(() => {
      const urlParams = new URLSearchParams();
      urlParams.set('size', '30');
      urlParams.set('page', '1');
      urlParams.set('sorting', '-id');
  
      API.fetch('GET', `/API/v1/groups?${urlParams.toString()}`).then(
        (data) => {
          setDefaultGroupOptions(data.objects.map((group: any) => {
            return {value: group.id, label: group.name};
          }));
        },
      ).catch((error) => showRequestError(error, t('Actor groups')));
    }, [t]);
  
  useEffect(() => {
    fetchGroupsOptions()
  }, [fetchGroupsOptions])

  const debouncedFetchCountries = useMemo(
    () => {
      let abortController: AbortController | null = null;

      return debounce((inputValue: string, callback: (options: any[]) => void) => {
        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();

        API.fetch('GET', `/API/v1/countries?q=${inputValue}`, { signal: abortController.signal } as any)
          .then((data) => {
            callback(
              data.objects.map((country: any) => ({
                value: country.id,
                label: country.name,
              }))
            );
          })
          .catch((error) => {
            showRequestError(error, t('Countries'));
            callback([]);
          });
      }, 300);
    },
    []
  );

  const debouncedFetchGroups = useMemo(
    () => {
      let abortController: AbortController | null = null;

      return debounce((inputValue: string, callback: (options: any[]) => void) => {
        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();

        API.fetch('GET', `/API/v1/groups?q=${inputValue}&sorting=-id`, { signal: abortController.signal } as any)
          .then((data) => {
            callback(
              data.objects.map((group: any) => ({
                value: group.id,
                label: group.name,
              }))
            );
          })
          .catch((error) => {
            showRequestError(error, t('Actor groups'));
            callback([]);
          });
      }, 300);
    },
    []
  );

  const debouncedFetchSources = useMemo(
    () => {
      let abortController: AbortController | null = null;

      return debounce((inputValue: string, callback: (options: any[]) => void) => {
        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();

        API.fetch('GET', `/API/v2/actors?q=${inputValue}&sorting=-relevance&${ALL_STATUSES_QUERY}`, undefined, undefined, abortController.signal)
          .then((data) => {
            callback(
              data.objects.map((source: any) => ({
                value: source.id,
                label: source.name,
                icon: platformIconsMapping[source.source_type] || platformIconsMapping['WEB'],
                followers_count: source.audience || 0,
              }))
            );
          })
          .catch((error) => {
            showRequestError(error, t('Sources'));
            callback([]);
          });
      }, 500);
    },
    []
  );

  const debouncedFetchFeeds = useMemo(
    () => {
      let abortController: AbortController | null = null;

      return debounce((inputValue: string, callback: (options: any[]) => void) => {
        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();

        API.fetch('GET', `/API/v1/feeds?q=${inputValue}`, { signal: abortController.signal } as any)
          .then((data) => {
            callback(
              data.objects
                .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
                .map((feed: any) => ({
                  value: feed.id,
                  label: feed.name,
                }))
            );
          })
          .catch((error) => {
            showRequestError(error, t('Feeds'));
            callback([]);
          });
      }, 300);
    },
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchCountries.cancel();
      debouncedFetchGroups.cancel();
      debouncedFetchSources.cancel();
      debouncedFetchFeeds.cancel();
    };
  }, [debouncedFetchCountries, debouncedFetchGroups, debouncedFetchSources, debouncedFetchFeeds]);

  return (
    <form.Subscribe
      selector={(state: any) => ({
        sourceGroups: state.values.sourceGroups,
        affiliationCountries: state.values.affiliationCountries,
        affiliationTypes: state.values.affiliationTypes,
        discreditations: state.values.discreditations,
        showBotsOnly: state.values.showBotsOnly,
        affiliationAll: state.values.affiliationAll,
        discreditationsExclude: state.values.discreditationsExclude,
      })}
      children={(formValues: any) => {
        const {
          sourceGroups,
          affiliationCountries,
          affiliationTypes,
          discreditations,
          showBotsOnly,
          affiliationAll,
          discreditationsExclude,
        } = formValues;

        // Exclusive filter logic
        const getActiveExclusiveFilter = () => {
          if (sourceGroups?.length > 0) return 'Actor groups';
          if (affiliationCountries?.length > 0 || affiliationTypes?.length > 0) return 'State-affiliated';
          if (discreditations?.length > 0) return 'High risk accounts';
          if (showBotsOnly === true) return 'Show only inauthentic actors';
          return null;
        };

        const activeExclusiveFilter = getActiveExclusiveFilter();
        const isFilterDisabled = (filterName: string) => {
          return activeExclusiveFilter && activeExclusiveFilter !== filterName;
        };

        const getDisabledTooltip = (filterName: string): string | undefined => {
          if (!isFilterDisabled(filterName)) return undefined;
          return t(`This filter can't be combined with '{{activeFilter}}'`, {
            activeFilter: t(activeExclusiveFilter || ''),
          });
        };

        const botsOptions = [
          { value: null, label: t('Include inauthentic actors') },
          { value: false, label: t('Exclude inauthentic actors') },
          { value: true, label: t('Show only inauthentic actors') },
        ];

        const promiseCountriesOptions = (
          inputValue: string,
        ): Promise<{ value: any; label: string }[]> => {
          if (!inputValue) {
            return Promise.resolve([]);
          }

          return new Promise((resolve) => {
            debouncedFetchCountries(inputValue, resolve);
          });
        };

        const promiseGroupsOptions = (
          inputValue: string,
        ): Promise<{ value: any; label: string }[]> => {
          if (!inputValue) {
            return Promise.resolve([]);
          }

          return new Promise((resolve) => {
            debouncedFetchGroups(inputValue, resolve);
          });
        };

        const promiseSourcesOptions = (
          inputValue: string,
        ): Promise<{ value: any; label: string }[]> => {
          if (!inputValue) {
            return Promise.resolve([]);
          }

          return new Promise((resolve) => {
            debouncedFetchSources(inputValue, resolve);
          });
        };

        const Option = (components as any).Option;
        const IconOption: React.FC<any> = (props: any) => {
          return (
            <Option {...props}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{display: 'inline-flex', alignItems: 'center', width: '16px', height: '16px', position: "relative", marginRight: '14px'}}>
                {props.data.icon}
                </span> {props.data.label} <b>({formatWithCustomGigaBillion(props.data.followers_count)})</b>
              </div>
              
            </Option>
          );
        };

        const promiseFeedsOptions = (
          inputValue: string,
        ): Promise<{ value: any; label: string }[]> => {
          if (!inputValue) {
            return Promise.resolve([]);
          }

          return new Promise((resolve) => {
            debouncedFetchFeeds(inputValue, resolve);
          });
        };

        return (
          <>
            <div className="form-group-header">{t('Actors')}</div>

            <div className="group-card">
              {showCountryGroupsFilter && (
                <div className="form-element">
                  <div className="labelContainer">
                    <label className="label">
                      {t('Actor origin country groups')}
                    </label>
                    <form.Field
                      name="geoGroupExclude"
                      children={(field: any) => (
                        <ExcludeSwitch
                          id="geoGroup-switch"
                          value={field.state.value || false}
                          onChange={(value: any) => field.handleChange(value)}
                          color="red"
                          direction={'row-reverse'}
                          isDisabled={isDisabled}
                        />
                      )}
                    />
                  </div>
                  <form.Field
                    name="geoGroup"
                    children={(field: any) => (
                      <GeoGroupFilter
                        selected={field.state.value || []}
                        disabled={isDisabled}
                        onChange={(checklist) => field.handleChange(checklist)}
                        style={{ fontSize: '16px' }}
                      />
                    )}
                  />
                </div>
              )}

              <div className="form-element">
                <div className="labelContainer">
                  <label className="label">{t('Actor origin countries')}</label>
                  <form.Field
                    name="countriesExclude"
                    children={(field: any) => (
                      <ExcludeSwitch
                        id="countries-switch"
                        value={field.state.value || false}
                        onChange={(value: any) => field.handleChange(value)}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={isDisabled}
                      />
                    )}
                  />
                </div>
                <form.Field
                  name="countries"
                  children={(field: any) => (
                    <Async
                      className="react-select-container"
                      classNamePrefix="react-select"
                      loadOptions={promiseCountriesOptions}
                      defaultOptions={countriesTop}
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder={t('Search or select countries')}
                      isMulti
                      isClearable
                      isSearchable
                      isDisabled={isDisabled}
                      cacheOptions
                      name="countries"
                      noOptionsMessage={() =>
                        t('Please enter at least 1 symbol')
                      }
                      loadingMessage={() => t('Loading...')}
                    />
                  )}
                />
              </div>

              <div
                className="form-element"
                data-field="sourceGroups"
                data-tooltip-id="actor-groups-tooltip"
                {...(getDisabledTooltip('Actor groups')
                  ? {
                      'data-tooltip-content':
                        getDisabledTooltip('Actor groups'),
                    }
                  : {})}
              >
                <div className="labelContainer">
                  <label className="label">{t('Actor groups')}</label>
                  <form.Field
                    name="sourceGroupsExclude"
                    children={(field: any) => (
                      <ExcludeSwitch
                        id="actor-groups-switch"
                        value={field.state.value || false}
                        onChange={(value: any) => field.handleChange(value)}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={
                          isFilterDisabled('Actor groups') || isDisabled || undefined
                        }
                      />
                    )}
                  />
                </div>
                <form.Field
                  name="sourceGroups"
                  children={(field: any) => (
                    <Async
                      className="react-select-container"
                      classNamePrefix="react-select"
                      loadOptions={promiseGroupsOptions}
                      defaultOptions={defaultGroupOptions}
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder={t('Search or select actor groups')}
                      isMulti
                      isDisabled={isFilterDisabled('Actor groups') || isDisabled || undefined}
                      isClearable
                      isSearchable={!isFilterDisabled('Actor groups') && !isDisabled}
                      cacheOptions
                      name="sourceGroups"
                      ref={actorGroupRef}
                    />
                  )}
                />
              </div>

              <div className="form-element" data-field="sources">
                <div className="labelContainer">
                  <label className="label">{t('Individual actors')}</label>
                  <form.Field
                    name="sourcesExclude"
                    children={(field: any) => (
                      <ExcludeSwitch
                        id="actor-switch"
                        value={field.state.value || false}
                        onChange={(value: any) => field.handleChange(value)}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={isDisabled}
                      />
                    )}
                  />
                </div>
                <form.Field
                  name="sources"
                  children={(field: any) => (
                    <Async
                      className="react-select-container"
                      classNamePrefix="react-select"
                      loadOptions={promiseSourcesOptions}
                      defaultOptions
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder={t('Search or select actors')}
                      components={{ Option: IconOption }}
                      isMulti
                      isClearable
                      isSearchable
                      isDisabled={isDisabled}
                      cacheOptions
                      name="sources"
                      noOptionsMessage={() =>
                        t('Please enter at least 1 symbol')
                      }
                      loadingMessage={() => t('Loading...')}
                      ref={actorRef}
                    />
                  )}
                />
              </div>

              {showTagsFilter && (
                <div className="form-element">
                  <div className="labelContainer">
                    <label className="label">{t('Actor tags')}</label>
                    <form.Field
                      name="tagsExclude"
                      children={(field: any) => (
                        <ExcludeSwitch
                          id="tags-switch"
                          value={field.state.value || false}
                          onChange={(value: any) => field.handleChange(value)}
                          color="red"
                          direction={'row-reverse'}
                          isDisabled={isDisabled}
                        />
                      )}
                    />
                  </div>
                  <form.Field
                    name="tags"
                    children={(field: any) => (
                      <TagsHierarchyChooser
                        selected={field.state.value || []}
                        disabled={isDisabled}
                        onChange={(value: string[]) => field.handleChange(value)}
                        mode="legacy"
                      />
                    )}
                  />
                </div>
              )}
            </div>

            <div className="form-group-header">{t('Actor behavior')}</div>

            <div className="group-card">
              {showFeedsFilter && (
                <div className="form-element">
                  <div className="labelContainer">
                    <label className="label">{t('Feeds')}</label>
                    <form.Field
                      name="feedsExclude"
                      children={(field: any) => (
                        <ExcludeSwitch
                          id="feeds-switch"
                          value={field.state.value || false}
                          onChange={(value: any) => field.handleChange(value)}
                          color="red"
                          direction={'row-reverse'}
                          isDisabled={isDisabled}
                        />
                      )}
                    />
                  </div>
                  <form.Field
                    name="feeds"
                    children={(field: any) => (
                      <Async
                        className="react-select-container"
                        classNamePrefix="react-select"
                        loadOptions={promiseFeedsOptions}
                        defaultOptions
                        value={field.state.value}
                        onChange={(value) => field.handleChange(value)}
                        onBlur={field.handleBlur}
                        placeholder={t('Search or select feeds')}
                        isMulti
                        isClearable
                        isSearchable
                        isDisabled={isDisabled}
                        cacheOptions
                        name="feeds"
                      />
                    )}
                  />
                </div>
              )}

              <div
                className="form-element"
                data-field="affiliationCountries"
                data-tooltip-id="state-affiliated-tooltip"
                {...(getDisabledTooltip('State-affiliated')
                  ? {
                      'data-tooltip-content':
                        getDisabledTooltip('State-affiliated'),
                    }
                  : {})}
              >
                <div className="labelContainer">
                  <label className="label">
                    {t('State-affiliated actors')}
                  </label>
                  <form.Field
                    name="affiliationCountriesExclude"
                    children={(field: any) => (
                      <ExcludeSwitch
                        id="affiliation-country-switch"
                        value={field.state.value || false}
                        onChange={(value: any) => field.handleChange(value)}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={
                          isFilterDisabled('State-affiliated') || isDisabled || undefined
                        }
                      />
                    )}
                  />
                </div>
                <form.Field
                  name="affiliationCountries"
                  children={(field: any) => (
                    <Async
                      className="react-select-container"
                      classNamePrefix="react-select"
                      loadOptions={promiseCountriesOptions}
                      defaultOptions={[
                        {
                          value: null,
                          label: t('Select all affiliation countries'),
                        },
                        ...countriesTop,
                      ]}
                      value={field.state.value}
                      onChange={(values) => {
                        if (isFilterDisabled('State-affiliated')) {
                          return;
                        } else if (values?.find((v: any) => v.value === null)) {
                          // Set affiliationAll to true
                          form.setFieldValue('affiliationAll', true);
                          // Set only the "Select all" option
                          field.handleChange([
                            {
                              value: null,
                              label: t('Select all affiliation countries'),
                            },
                          ]);
                        } else {
                          // Set affiliationAll to false when individual countries are selected
                          form.setFieldValue('affiliationAll', false);
                          field.handleChange(values);
                        }
                      }}
                      onBlur={field.handleBlur}
                      placeholder={t('Search or select countries')}
                      isMulti
                      isDisabled={
                        isFilterDisabled('State-affiliated') || isDisabled || undefined
                      }
                      isClearable
                      isSearchable={
                        !(
                          affiliationAll || isFilterDisabled('State-affiliated') || isDisabled
                        )
                      }
                      isOptionDisabled={() => affiliationAll}
                      cacheOptions
                      name="affiliationCountries"
                      noOptionsMessage={() =>
                        t('Please enter at least 1 symbol')
                      }
                      loadingMessage={() => t('Loading...')}
                    />
                  )}
                />
              </div>

              <div
                className="form-element"
                data-field="affiliationTypes"
                data-tooltip-id="state-affiliated-tooltip"
                {...(getDisabledTooltip('State-affiliated')
                  ? {
                      'data-tooltip-content':
                        getDisabledTooltip('State-affiliated'),
                    }
                  : {})}
              >
                <div className="labelContainer">
                  <label className="label">
                    {t('Affiliation Category')}
                  </label>
                  <form.Field
                    name="affiliationTypesExclude"
                    children={(field: any) => (
                      <ExcludeSwitch
                        id="affiliation-types-switch"
                        value={field.state.value || false}
                        onChange={(value: any) => field.handleChange(value)}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={
                          isFilterDisabled('State-affiliated') || isDisabled || undefined
                        }
                      />
                    )}
                  />
                </div>
                <form.Field
                  name="affiliationTypes"
                  children={(field: any) => (
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={affiliationTypesOptions(t)}
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder={t('Select affiliation categories')}
                      isMulti
                      isDisabled={
                        isFilterDisabled('State-affiliated') || isDisabled || undefined
                      }
                      isClearable
                      isSearchable={!isFilterDisabled('State-affiliated') && !isDisabled}
                      name="affiliationTypes"
                    />
                  )}
                />
              </div>

              {!hideRiskActors && (
                <div
                  className="form-element"
                  data-field="discreditations"
                  data-tooltip-id="high-risk-accounts-tooltip"
                  {...(getDisabledTooltip('High risk accounts')
                    ? {
                        'data-tooltip-content':
                          getDisabledTooltip('High risk accounts'),
                      }
                    : {})}
                >
                  <div className="labelContainer">
                    <label className="label">{t('High risk accounts')}</label>
                    <form.Field
                      name="discreditationsExclude"
                      children={(field: any) => (
                        <ExcludeSwitch
                          id="discreditations-switch"
                          value={field.state.value || false}
                          onChange={(value: any) => field.handleChange(value)}
                          color="red"
                          direction={'row-reverse'}
                          isDisabled={
                            isFilterDisabled('High risk accounts') || isDisabled || undefined
                          }
                        />
                      )}
                    />
                  </div>
                  <form.Field
                    name="discreditations"
                    children={(field: any) => (
                      <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={IoCTypeOptionsList(t)}
                        value={field.state.value}
                        onChange={(value) => field.handleChange(value)}
                        onBlur={field.handleBlur}
                        placeholder={t('Select one or more risk types')}
                        isMulti
                        isDisabled={
                          isFilterDisabled('High risk accounts') || isDisabled || undefined
                        }
                        isClearable
                        isSearchable={!isFilterDisabled('High risk accounts') && !isDisabled}
                        name="discreditations"
                      />
                    )}
                  />
                </div>
              )}

              <div className="form-element">
                <label className="label">{t('Inauthentic behavior')}</label>
                <form.Field
                  name="showBotsOnly"
                  children={(field: any) => (
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={botsOptions.map((option) => ({
                        ...option,
                        isDisabled:
                          option.value === true &&
                          isFilterDisabled('Show only inauthentic behavior'),
                      }))}
                      value={botsOptions.find(
                        (o) => o.value === field.state.value,
                      )}
                      onChange={(v: any) => {
                        if (
                          v.value === true &&
                          isFilterDisabled('Show only inauthentic behavior')
                        ) {
                          return;
                        }
                        field.handleChange(v.value);
                      }}
                      onBlur={field.handleBlur}
                      placeholder={t('Select inauthentic behavior')}
                      isClearable={false}
                      isDisabled={isDisabled}
                      name="showBotsOnly"
                    />
                  )}
                />
                {/* Tooltips for disabled filters */}
                {['Actor groups', 'State-affiliated', 'High risk accounts'].map(
                  (filterName) =>
                    isFilterDisabled(filterName) && (
                      <Tooltip
                        key={filterName}
                        id={`${filterName
                          .toLowerCase()
                          .replace(/\s+/g, '-')}-tooltip`}
                        place="top"
                        style={{
                          fontSize: '14px',
                          textAlign: 'center',
                          marginTop: '1.5rem',
                        }}
                      />
                    ),
                )}

                {/* Special tooltip for "Show only" option when disabled */}
                {isFilterDisabled('Show only inauthentic behavior') && (
                  <Tooltip
                    id="show-only-disabled-tooltip"
                    place="top"
                    style={{
                      fontSize: '14px',
                      textAlign: 'center',
                      marginTop: '1.5rem',
                    }}
                  />
                )}
              </div>
            </div>
          </>
        );
      }}
    />
  );
};
